import type { TFunction } from "i18next";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { useChoiceOptions } from "modules/record/shared/hooks/useChoiceOptions";
import { FlussCatalogOutput, FlussDataCatalog } from "modules/fluss/types/data-catalog";
import { areScopeTypesCompatible, ScopeOption } from "../fluss-builder-types";
import useTableColumns from "./useTableColumns";
import { pickerOutputLabel } from "./outputLabel";

export { pickerOutputLabel };

// Maximum number of path segments walked through reference fields (grammar C3).
export const MAX_PATH_DEPTH = 3;

export interface PickerOutput {
    path: string;
    key: string;
    label: string;
    labelKey?: string;
    type: string;
    referencedTable?: string;
    options?: { value: string; label: string }[];
    columnId?: string;
    // Number of segments already used after the scope root (record.a.b = 2, var.Node.key.a = 1).
    depth: number;
}

export interface PickerGroup {
    key: string;
    kind: "record" | "process" | "node" | "extra";
    label: string;
    table?: string;
    outputs: PickerOutput[];
}

// What the picker knows about the value it emitted/holds; used by callers that need the leaf type
// (condition builder editors) without re-deriving it.
export interface DataPickerMeta {
    path: string;
    label: string;
    type: string;
    referencedTable?: string;
    options?: { value: string; label: string }[];
    columnId?: string;
}

const toPickerOutput = (output: FlussCatalogOutput, depth: number): PickerOutput => ({
    path: output.path,
    key: output.key,
    label: output.label,
    labelKey: output.label_key,
    type: output.type || "TEXT",
    referencedTable: output.referenced_table_name || undefined,
    options: output.options,
    columnId: output.column_id,
    depth,
});

export const scopeOptionToOutput = (field: ScopeOption): PickerOutput => ({
    path: `record.${field.name}`,
    key: field.name,
    label: field.label,
    type: field.type,
    referencedTable: field.referencedTableName,
    columnId: field.id,
    depth: 1,
});

export interface ExtraGroup {
    key: string;
    label: string;
    outputs: { path: string; label: string; type: string; referencedTable?: string }[];
}

export const buildPickerGroups = (
    catalog: FlussDataCatalog,
    t: TFunction,
    options: {
        recordOutputs?: PickerOutput[];
        recordTable?: string | null;
        recordTableLabel?: string;
        recordSourceLabel?: string;
        includeRecord?: boolean;
        // Only the record columns (observed-table conditions evaluate against the record alone).
        recordOnly?: boolean;
        extraGroups?: ExtraGroup[];
    } = {}
): PickerGroup[] => {
    const groups: PickerGroup[] = [];
    const recordTable = options.recordTable ?? catalog.record?.table ?? null;
    const recordOutputs = (options.recordOutputs
        ?? (catalog.record?.outputs || []).map((output) => toPickerOutput(output, 1)))
        .map((output) => output.path === "record.id" && recordTable
            ? { ...output, referencedTable: recordTable } : output);
    if (options.includeRecord !== false && (recordTable || recordOutputs.length > 0)) {
        groups.push({
            key: "record",
            kind: "record",
            table: recordTable || undefined,
            label: recordTable && options.recordSourceLabel
                ? String(t("fluss.data.picker.observed_record_group", {
                    defaultValue: "{{source}} › Registro ({{table}})", source: options.recordSourceLabel,
                    table: options.recordTableLabel || recordTable,
                }))
                : recordTable
                ? String(t("fluss.data.picker.record_group", {
                    defaultValue: "Registro do processo ({{table}})", table: options.recordTableLabel || recordTable,
                }))
                : String(t("fluss.data.picker.record_group_no_table", { defaultValue: "Registro do processo" })),
            outputs: recordOutputs,
        });
    }
    if (options.recordOnly) return groups;
    const processOutputs = catalog.process_variables?.outputs || [];
    if (processOutputs.length > 0) {
        groups.push({
            key: "process",
            kind: "process",
            label: String(t(catalog.process_variables.label_key, { defaultValue: "Variáveis do processo" })),
            outputs: processOutputs.map((output) => toPickerOutput(output, 0)),
        });
    }
    (catalog.nodes || []).forEach((node) => {
        if (!node.outputs?.length) return;
        groups.push({
            key: `node:${node.node_id}`,
            kind: "node",
            label: node.node_label,
            outputs: node.outputs.map((output) => toPickerOutput(output, 0)),
        });
    });
    (options.extraGroups || []).forEach((group) => groups.push({
        key: group.key,
        kind: "extra",
        label: group.label,
        outputs: group.outputs.map((output) => ({
            path: output.path, key: output.path, label: output.label, type: output.type,
            referencedTable: output.referencedTable, depth: 0,
        })),
    }));
    return groups;
};

// An output is selectable when its type fits the expectation (C9 type filter). `referencedTable`
// restricts to records of that table.
export const tableDescendsFrom = (
    table: string | undefined,
    ancestor: string,
    parents: Record<string, string | undefined>
): boolean => {
    if (!table) return false;
    const visited = new Set<string>();
    let current: string | undefined = table;
    while (current && !visited.has(current)) {
        if (current === ancestor) return true;
        visited.add(current);
        current = parents[current];
    }
    return false;
};

export const outputSelectable = (
    output: Pick<PickerOutput, "type" | "referencedTable">,
    expectedType?: string,
    referencedTable?: string,
    allowedTypes?: string[],
    tableParents: Record<string, string | undefined> = {}
): boolean => {
    if (allowedTypes && !allowedTypes.includes(output.type)) return false;
    // The table check only applies to REFERENCE sources; a TEXT/CODE source is taken as the id (F4-U01).
    if (referencedTable) {
        if (output.type === "REFERENCE") return tableDescendsFrom(output.referencedTable, referencedTable, tableParents);
        if (output.type === "UUID" && output.referencedTable) {
            return tableDescendsFrom(output.referencedTable, referencedTable, tableParents);
        }
        // F4-U02: a UUID source (e.g. a record's "ID") is the id too; the publish validator checks its table.
        return ["TEXT", "CODE", "UUID"].includes(output.type) && ["UUID", "REFERENCE"].includes(expectedType || "")
            && areScopeTypesCompatible(expectedType || "", output.type);
    }
    if (expectedType === "TEMPLATE_TEXT") return output.type === "REFERENCE" || areScopeTypesCompatible("TEXT", output.type);
    if (!expectedType) return true;
    return areScopeTypesCompatible(expectedType, output.type);
};

export const outputExpandable = (output: Pick<PickerOutput, "type" | "referencedTable" | "depth">): boolean =>
    output.type === "REFERENCE" && Boolean(output.referencedTable) && output.depth < MAX_PATH_DEPTH;

export const isDataReference = (value: unknown): value is string =>
    typeof value === "string" && (value === "record" || /^(record|var|payload)\./.test(value));

export type CurrentDateUnit = "minute" | "hour" | "day" | "week" | "month" | "year";
export const CURRENT_DATE_UNITS: CurrentDateUnit[] = ["minute", "hour", "day", "week", "month", "year"];
export const parseCurrentDateToken = (value: unknown) => {
    if (typeof value !== "string") return null;
    const match = /^@(now|today)(?:([+-])(\d+)(minute|hour|day|week|month|year))?$/.exec(value);
    if (!match) return null;
    return { base: match[1] as "now" | "today", sign: (match[2] || "+") as "+" | "-",
        amount: match[3] ? Number(match[3]) : 0, unit: (match[4] || "day") as CurrentDateUnit };
};

export interface DataPathInfo extends DataPickerMeta {
    parts: string[];
    known: boolean;
}

// Resolves a stored reference ("record.manager_id.email", "var.Find_1.record.name") to readable
// labels (joined with " › " in the chip) and to the leaf type. The first segment comes from the
// catalog; deeper segments are looked up lazily in the columns of the referenced tables (at most
// three levels, so three fixed hook calls).
export const useDataPathInfo = (value: unknown, groups: PickerGroup[], t: TFunction): DataPathInfo | null => {
    const path = isDataReference(value) ? value : "";
    let base: PickerOutput | undefined;
    let group: PickerGroup | undefined;
    groups.forEach((candidate) => candidate.outputs.forEach((output) => {
        if ((path === output.path || path.startsWith(`${output.path}.`)) && (!base || output.path.length > base.path.length)) {
            base = output;
            group = candidate;
        }
    }));
    const rest = base && path.length > base.path.length ? path.slice(base.path.length + 1).split(".") : [];

    const level1 = useTableColumns(rest.length >= 1 ? base?.referencedTable : undefined);
    const column1 = rest.length >= 1 ? level1.columns.find((column) => column.name === rest[0]) : undefined;
    const level2 = useTableColumns(rest.length >= 2 ? column1?.referencedTableName : undefined);
    const column2 = rest.length >= 2 ? level2.columns.find((column) => column.name === rest[1]) : undefined;
    const level3 = useTableColumns(rest.length >= 3 ? column2?.referencedTableName : undefined);
    const column3 = rest.length >= 3 ? level3.columns.find((column) => column.name === rest[2]) : undefined;
    // Catalog record outputs may have no inline choices. Resolve their real column on reload;
    // reference leaves already arrive through the lazy table-column lookup above.
    const recordColumns = useTableColumns(rest.length === 0 && group?.kind === "record" && base?.type === "CHOICE"
        && !base.options?.length ? group.table : undefined);
    const directColumn = recordColumns.columns.find((column) => column.name === base?.key);
    const leaf = rest.length === 0 ? undefined : [column1, column2, column3][rest.length - 1];
    const choiceColumn = rest.length === 0 ? directColumn?.column : leaf?.column;
    const { data: choiceOptions = [] } = useChoiceOptions(choiceColumn?.column_type === "CHOICE" ? choiceColumn : undefined);

    if (!path) return null;
    if (!base || !group) return { parts: [path], known: false, path, label: path, type: "TEXT" };

    const columns = [column1, column2, column3];
    const navigated = rest.map((segment, index) => {
        const column = columns[index];
        return column ? (translateMetadataLabel(t, column) || column.label || segment) : segment;
    });
    const head = pickerOutputLabel(t, base);
    const parts = [...(group.kind === "node" ? [group.label] : []), head, ...navigated];
    const resolved = rest.length === 0 ? base : undefined;
    return {
        parts,
        known: rest.length === 0 || Boolean(leaf),
        path,
        label: parts.join(" › "),
        type: resolved ? resolved.type : (leaf?.type || "TEXT"),
        referencedTable: resolved ? resolved.referencedTable : leaf?.referencedTableName,
        options: resolved?.options || (choiceOptions.length ? choiceOptions : undefined),
        columnId: resolved ? resolved.columnId : leaf?.id,
    };
};
